// donutChart — radial pie with hole.

import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import {
  applySelectionStyle,
  baseChartOption,
  bucketKey,
  firstFieldRef,
  isEmpty,
} from "./_echartsHelpers";

export function DonutChartVisual({ spec, data, filters, onFilter }: VisualProps) {
  const empty = isEmpty(data);
  const rows = data.rows ?? [];
  const fields = spec.fields ?? {};
  const categoryKey = bucketKey(fields.Category) ?? bucketKey(fields.Group);
  const valueKey = bucketKey(fields.Values) ?? bucketKey(fields.Y);

  const seriesData =
    categoryKey && valueKey
      ? rows.map((r) => ({
          name: String(r[categoryKey] ?? ""),
          value: Number(r[valueKey] ?? 0),
        }))
      : [];

  const categoryField = firstFieldRef(fields.Category) ?? firstFieldRef(fields.Group);

  // AGENT-C: pie/donut has no cartesian grid or axes — null them out so the
  // base axis defaults don't paint an invisible empty grid.
  const base = baseChartOption();
  const baseOption: EChartsOption = {
    ...base,
    xAxis: undefined,
    yAxis: undefined,
    grid: undefined,
    // AGENT-C: keep the PBI tooltip chrome (white surface, hairline border,
    // Segoe UI 12) but switch trigger to "item" for slice-level hover.
    tooltip: { ...(base.tooltip as object), trigger: "item" },
    series: [
      {
        type: "pie",
        radius: ["45%", "75%"],
        avoidLabelOverlap: true,
        label: { show: false },
        labelLine: { show: false },
        data: seriesData,
      },
    ],
  };

  // P1-selection: dim non-selected slices when a matching filter is active.
  // Category lives on each row's .name (no axis on pie), so we pass those.
  const categoryKeyFq = categoryField?.table && categoryField?.column
    ? `${categoryField.table}.${categoryField.column}`
    : categoryKey;
  const option = applySelectionStyle(
    baseOption,
    filters ?? {},
    categoryKeyFq,
    seriesData.map((d) => d.name),
  );

  // Use a custom click handler for pies (params.name comes from the slice).
  const onClick = (params: { name?: string }) => {
    if (!categoryField?.table || !categoryField?.column || !params.name) return;
    onFilter(`${categoryField.table}.${categoryField.column}`, {
      op: "eq",
      value: params.name,
      source: spec.id,
    });
  };

  return (
    <ChartShell title={spec.title} isEmpty={empty} status={data.status}>
      <ReactECharts
        option={option}
        notMerge
        style={{ height: "100%", width: "100%" }}
        onEvents={{ click: onClick }}
      />
    </ChartShell>
  );
}
